export const SAVED_FILTERS_KEY = "plp-saved-filters";

export const EXCLUDED_FACET_IDS = new Set(["currentprice", "floor"]);

const isClientSide = () => typeof window !== "undefined";

export const saveFiltersToSession = (key, filters) => {
  if (!isClientSide() || !key) return;

  try {
    sessionStorage.setItem(key, JSON.stringify(filters));
  } catch {
    // Silently fail, e.g. if storage is full or user is in private mode
  }
};

export const getFiltersFromSession = (key) => {
  if (!isClientSide() || !key) return null;

  try {
    const stored = sessionStorage.getItem(key);

    return stored ? JSON.parse(stored) : null;
  } catch {
    // Silently fail, e.g. if parsing fails
    return null;
  }
};

export const clearFiltersFromSession = (key) => {
  if (!isClientSide() || !key) return;

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Silently fail
  }
};

const parseRefineToMap = (refine) => {
  if (!refine || typeof refine !== "string") return Object.create(null);

  return refine.split("|").reduce((map, facetString) => {
    const colonIndex = facetString.indexOf(":");
    if (colonIndex === -1) return map;

    const facetId = facetString.substring(0, colonIndex);
    if (facetId in Object.prototype) return map;

    const values = facetString.substring(colonIndex + 1);
    map[facetId] = values;

    return map;
  }, Object.create(null));
};

const buildRefineFromMap = (map) => {
  const entries = Object.entries(map);
  if (entries.length === 0) return undefined;

  return entries.map(([facetId, values]) => `${facetId}:${values}`).join("|");
};

export const mergeFiltersWithUrl = (urlQuery = {}, savedFilters = {}) => {
  const urlRefineMap = parseRefineToMap(urlQuery.refine);
  const savedRefineMap = parseRefineToMap(savedFilters.refine);

  const mergedRefineMap = { ...savedRefineMap, ...urlRefineMap };
  const mergedRefine = buildRefineFromMap(mergedRefineMap);

  return {
    refine: mergedRefine,
  };
};

export const getSavedFilters = () => getFiltersFromSession(SAVED_FILTERS_KEY);

export const syncSavedFilters = ({
  refine,
  facets = [],
  removeUnapplied = true,
} = {}) => {
  if (!isClientSide()) return;

  const saved = getFiltersFromSession(SAVED_FILTERS_KEY) || {};
  const savedMap = parseRefineToMap(saved.refine);
  const refineMap = parseRefineToMap(refine);

  facets.forEach((facet) => {
    const facetId = facet.id;
    if (EXCLUDED_FACET_IDS.has(facetId) || facetId in Object.prototype) return;

    if (facetId in refineMap) {
      savedMap[facetId] = removeUnapplied
        ? refineMap[facetId]
        : [
            ...new Set(
              [savedMap[facetId], refineMap[facetId]]
                .filter(Boolean)
                .flatMap((values) => values.split(",")),
            ),
          ].join(",");

      return;
    }

    // Only a deliberate user interaction (deselecting a filter or clearing all)
    // may remove a saved filter. Plain navigation between listing pages must
    // never delete carry-over filters, otherwise a value that is simply not
    // applicable on the visited page (e.g. a "shirt" top type while browsing
    // dresses) would be lost and fail to re-apply when the shopper returns.
    if (!removeUnapplied) return;

    if (!(facetId in savedMap)) return;

    // Even during an interaction, only treat the absence as a removal when the
    // saved value was actually offered on this page. A carry-over value that is
    // not offered here must be preserved.
    const availableValues = new Set(
      (facet.facetValues ?? []).map((facetValue) => String(facetValue.id)),
    );
    const savedValueWasOffered = savedMap[facetId]
      .split(",")
      .every((value) => availableValues.has(value));

    if (savedValueWasOffered) {
      delete savedMap[facetId];
    }
  });

  const nextRefine = buildRefineFromMap(savedMap);

  if (nextRefine) {
    saveFiltersToSession(SAVED_FILTERS_KEY, { refine: nextRefine });
  } else {
    clearFiltersFromSession(SAVED_FILTERS_KEY);
  }
};
